import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { getBaseURL } from "@scoutus/sports-metrics";
import type {
  ActiveGuidance,
  CopyPromptCollectionRequest,
  CreateDocumentRequest,
  CreatePersonaRequest,
  CreateTestRunRequest,
  DocumentSummary,
  GapAskers,
  DocumentVersion,
  DocumentVersionSummary,
  GuidanceVersion,
  ImportPromptsRequest,
  ImportUrlRequest,
  IndexingResult,
  LayoutItem,
  LibraryCategoryInfo,
  LibraryDocument,
  LibraryGap,
  ListDocumentsQuery,
  ListGapsQuery,
  ListPromptCollectionsQuery,
  ListTestRunsQuery,
  Persona,
  PromptCollection,
  PromptCollectionDetail,
  PromptCollectionInput,
  PromptCollectionSummary,
  PromptGroup,
  PromptGroupInput,
  PromptInput,
  ReindexAllResult,
  ReleaseSportRequest,
  SearchTestAthletesQuery,
  SetTestVerdictRequest,
  SimilarPrompt,
  SportRelease,
  TestAthlete,
  TestAthletePreview,
  TestPrompt,
  UsageQuery,
  UsageReport,
  TestRun,
  TestRunAiReviewResponse,
  TestRunAnswer,
  TestRunComparison,
  TestRunDetail,
  TestRunOptions,
  UpdateDocumentRequest,
  UpdateTestRunRequest,
  UpdatePersonaRequest,
  UploadDocumentRequest,
} from "../types/ai-coach";

/**
 * Client for the AI coach admin API.
 *
 * Goes straight to axios rather than through `@scoutus/sports-metrics`
 * because the generated client is rebuilt from the API's OpenAPI spec by a
 * separate publish and does not include these routes yet. It shares the
 * package's base URL and receives the same Auth0 access token from
 * AuthProvider, so behaviour matches the generated calls. Swap for
 * `api.adminAiCoach.*` once the package is regenerated.
 */
const client = axios.create();

let authToken: string | null = null;

/** Called by AuthProvider whenever it refreshes the Auth0 access token. */
export function setAiCoachAuthToken(token: string | null): void {
  authToken = token;
}

client.interceptors.request.use((config) => {
  config.baseURL = getBaseURL();
  if (authToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

/** The API's `{ errorCode, message }` body, or a plain message. */
export function aiCoachErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = (
      error as AxiosError<{ message?: unknown; errorCode?: string }>
    ).response?.data;
    if (data && typeof data === "object" && "message" in data) {
      const message = Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message);
      return data.errorCode ? `${message} (${data.errorCode})` : message;
    }
    if (error.request && !error.response) return "Cannot reach the API.";
  }
  return error instanceof Error ? error.message : "Something went wrong";
}

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await client.request<T>({
    ...config,
    url: `/api/v1/admin/ai-coach${config.url ?? ""}`,
  });
  return response.data;
}

/**
 * Saves a file the API sends. Its name comes from Content-Disposition when
 * the API exposes it to the browser, else `fallbackName`.
 */
async function download(url: string, fallbackName: string): Promise<void> {
  const response = await client.request<Blob>({
    method: "GET",
    url: `/api/v1/admin/ai-coach${url}`,
    responseType: "blob",
  });
  const disposition = String(response.headers["content-disposition"] ?? "");
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName;
  const href = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

function clean<T extends object>(params: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(params) as Array<keyof T>).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  });
  return out;
}

class AiCoachService {
  // ------------------------------------------------------------- library
  listCategories(): Promise<LibraryCategoryInfo[]> {
    return request({ method: "GET", url: "/library/categories" });
  }

  listDocuments(query: ListDocumentsQuery = {}): Promise<DocumentSummary[]> {
    return request({ method: "GET", url: "/documents", params: clean(query) });
  }

  getDocument(id: number): Promise<LibraryDocument> {
    return request({ method: "GET", url: `/documents/${id}` });
  }

  createDocument(body: CreateDocumentRequest): Promise<LibraryDocument> {
    return request({ method: "POST", url: "/documents", data: body });
  }

  importUrl(body: ImportUrlRequest): Promise<LibraryDocument> {
    return request({
      method: "POST",
      url: "/documents/import-url",
      data: body,
    });
  }

  uploadDocument(body: UploadDocumentRequest): Promise<LibraryDocument> {
    const form = new FormData();
    form.append("file", body.file);
    form.append("category", body.category);
    if (body.title) form.append("title", body.title);
    if (body.sport_id) form.append("sport_id", body.sport_id);
    if (body.division_scope && body.division_scope.length > 0) {
      form.append("division_scope", JSON.stringify(body.division_scope));
    }
    if (body.gender_scope && body.gender_scope.length > 0) {
      form.append("gender_scope", JSON.stringify(body.gender_scope));
    }
    return request({
      method: "POST",
      url: "/documents/upload",
      data: form,
    });
  }

  updateDocument(
    id: number,
    body: UpdateDocumentRequest,
  ): Promise<LibraryDocument> {
    return request({ method: "PATCH", url: `/documents/${id}`, data: body });
  }

  publishDocument(id: number): Promise<LibraryDocument> {
    return request({ method: "POST", url: `/documents/${id}/publish` });
  }

  unpublishDocument(id: number): Promise<LibraryDocument> {
    return request({ method: "POST", url: `/documents/${id}/unpublish` });
  }

  archiveDocument(id: number): Promise<LibraryDocument> {
    return request({ method: "POST", url: `/documents/${id}/archive` });
  }

  /** One step back: discards the pending draft, or archives a document with none. */
  deleteDocument(id: number): Promise<LibraryDocument> {
    return request({ method: "DELETE", url: `/documents/${id}` });
  }

  listDocumentVersions(id: number): Promise<DocumentVersionSummary[]> {
    return request({ method: "GET", url: `/documents/${id}/versions` });
  }

  getDocumentVersion(id: number, versionId: number): Promise<DocumentVersion> {
    return request({
      method: "GET",
      url: `/documents/${id}/versions/${versionId}`,
    });
  }

  restoreDocumentVersion(
    id: number,
    versionId: number,
  ): Promise<LibraryDocument> {
    return request({
      method: "POST",
      url: `/documents/${id}/versions/${versionId}/restore`,
    });
  }

  reindexDocument(id: number): Promise<IndexingResult> {
    return request({ method: "POST", url: `/documents/${id}/reindex` });
  }

  reindexAll(): Promise<ReindexAllResult> {
    return request({ method: "POST", url: "/library/reindex" });
  }

  // ---------------------------------------------------------------- gaps
  /** Usage and cost over the last N days; costs from ai_coach_model_prices. */
  getUsage(query: UsageQuery = {}): Promise<UsageReport> {
    return request({ method: "GET", url: "/usage", params: clean(query) });
  }

  listGaps(query: ListGapsQuery = {}): Promise<LibraryGap[]> {
    return request({ method: "GET", url: "/gaps", params: clean(query) });
  }

  /** Who hit a gap, with each athlete's division and gender now. */
  getGapAskers(id: number): Promise<GapAskers> {
    return request({ method: "GET", url: `/gaps/${id}/askers` });
  }

  resolveGap(
    id: number,
    documentId: number,
    note?: string | null,
  ): Promise<LibraryGap> {
    return request({
      method: "POST",
      url: `/gaps/${id}/resolve`,
      data: { document_id: documentId, note: note || undefined },
    });
  }

  markGapBoundary(id: number, note?: string | null): Promise<LibraryGap> {
    return request({
      method: "POST",
      url: `/gaps/${id}/boundary`,
      data: { note: note || undefined },
    });
  }

  reopenGap(id: number): Promise<LibraryGap> {
    return request({ method: "POST", url: `/gaps/${id}/reopen` });
  }

  // ------------------------------------------------------------ personas
  listPersonas(): Promise<Persona[]> {
    return request({ method: "GET", url: "/personas" });
  }

  createPersona(body: CreatePersonaRequest): Promise<Persona> {
    return request({ method: "POST", url: "/personas", data: body });
  }

  updatePersona(id: number, body: UpdatePersonaRequest): Promise<Persona> {
    return request({ method: "PATCH", url: `/personas/${id}`, data: body });
  }

  publishPersona(id: number): Promise<Persona> {
    return request({ method: "POST", url: `/personas/${id}/publish` });
  }

  unpublishPersona(id: number): Promise<Persona> {
    return request({ method: "POST", url: `/personas/${id}/unpublish` });
  }

  archivePersona(id: number): Promise<Persona> {
    return request({ method: "POST", url: `/personas/${id}/archive` });
  }

  uploadPersonaPortrait(id: number, file: File): Promise<Persona> {
    const form = new FormData();
    form.append("file", file);
    return request({
      method: "POST",
      url: `/personas/${id}/portrait`,
      data: form,
    });
  }

  removePersonaPortrait(id: number): Promise<Persona> {
    return request({ method: "DELETE", url: `/personas/${id}/portrait` });
  }

  // ------------------------------------------------------------ guidance
  listGuidance(): Promise<GuidanceVersion[]> {
    return request({ method: "GET", url: "/guidance" });
  }

  getActiveGuidance(): Promise<ActiveGuidance> {
    return request({ method: "GET", url: "/guidance/active" });
  }

  createGuidance(content: string): Promise<GuidanceVersion> {
    return request({ method: "POST", url: "/guidance", data: { content } });
  }

  // --------------------------------------------------------- prompt bank
  listPromptCollections(
    query: ListPromptCollectionsQuery = {},
  ): Promise<PromptCollectionSummary[]> {
    return request({
      method: "GET",
      url: "/prompt-collections",
      params: clean({
        sport_id: query.sport_id,
        include_archived: query.include_archived ? "true" : undefined,
      }),
    });
  }

  getPromptCollection(id: number): Promise<PromptCollectionDetail> {
    return request({ method: "GET", url: `/prompt-collections/${id}` });
  }

  createPromptCollection(
    body: PromptCollectionInput,
  ): Promise<PromptCollection> {
    return request({ method: "POST", url: "/prompt-collections", data: body });
  }

  updatePromptCollection(
    id: number,
    body: Partial<PromptCollectionInput>,
  ): Promise<PromptCollection> {
    return request({
      method: "PATCH",
      url: `/prompt-collections/${id}`,
      data: body,
    });
  }

  archivePromptCollection(id: number): Promise<PromptCollection> {
    return request({
      method: "POST",
      url: `/prompt-collections/${id}/archive`,
    });
  }

  unarchivePromptCollection(id: number): Promise<PromptCollection> {
    return request({
      method: "POST",
      url: `/prompt-collections/${id}/unarchive`,
    });
  }

  copyPromptCollection(
    id: number,
    body: CopyPromptCollectionRequest,
  ): Promise<PromptCollection> {
    return request({
      method: "POST",
      url: `/prompt-collections/${id}/copy`,
      data: body,
    });
  }

  setPromptLayout(
    id: number,
    items: LayoutItem[],
  ): Promise<PromptCollectionDetail> {
    return request({
      method: "PUT",
      url: `/prompt-collections/${id}/layout`,
      data: { items },
    });
  }

  importPrompts(id: number, body: ImportPromptsRequest): Promise<TestPrompt[]> {
    return request({
      method: "POST",
      url: `/prompt-collections/${id}/import`,
      data: body,
    });
  }

  createPromptGroup(
    collectionId: number,
    body: PromptGroupInput,
  ): Promise<PromptGroup> {
    return request({
      method: "POST",
      url: `/prompt-collections/${collectionId}/groups`,
      data: body,
    });
  }

  updatePromptGroup(
    id: number,
    body: Partial<PromptGroupInput>,
  ): Promise<PromptGroup> {
    return request({
      method: "PATCH",
      url: `/prompt-groups/${id}`,
      data: body,
    });
  }

  deletePromptGroup(id: number): Promise<void> {
    return request({ method: "DELETE", url: `/prompt-groups/${id}` });
  }

  createTestPrompt(
    collectionId: number,
    body: PromptInput,
  ): Promise<TestPrompt> {
    return request({
      method: "POST",
      url: `/prompt-collections/${collectionId}/prompts`,
      data: body,
    });
  }

  updateTestPrompt(
    id: number,
    body: Omit<Partial<PromptInput>, "group_id">,
  ): Promise<TestPrompt> {
    return request({ method: "PATCH", url: `/test-prompts/${id}`, data: body });
  }

  /** The list's questions closest in meaning to a draft; `excludeId` is the one being edited. */
  findSimilarPrompts(
    collectionId: number,
    text: string,
    excludeId: number | null,
  ): Promise<SimilarPrompt[]> {
    return request({
      method: "POST",
      url: `/prompt-collections/${collectionId}/similar-prompts`,
      data: { text, exclude_prompt_id: excludeId },
    });
  }

  deleteTestPrompt(id: number): Promise<void> {
    return request({ method: "DELETE", url: `/test-prompts/${id}` });
  }

  searchTestAthletes(query: SearchTestAthletesQuery): Promise<TestAthlete[]> {
    return request({
      method: "GET",
      url: "/test-athletes",
      params: clean(query),
    });
  }

  previewTestAthlete(id: number, sportId: string): Promise<TestAthletePreview> {
    return request({
      method: "GET",
      url: `/test-athletes/${id}/preview`,
      params: { sport_id: sportId },
    });
  }

  getTestRunOptions(): Promise<TestRunOptions> {
    return request({ method: "GET", url: "/test-runs/options" });
  }

  listTestRuns(query: ListTestRunsQuery = {}): Promise<TestRun[]> {
    return request({ method: "GET", url: "/test-runs", params: clean(query) });
  }

  createTestRun(body: CreateTestRunRequest): Promise<TestRun> {
    return request({ method: "POST", url: "/test-runs", data: body });
  }

  getTestRun(id: number): Promise<TestRunDetail> {
    return request({ method: "GET", url: `/test-runs/${id}` });
  }

  /**
   * Sends the run's next turns and AI reviews, and returns the run once they
   * are done. The run only moves forward while this is called.
   */
  advanceTestRun(id: number): Promise<TestRunDetail> {
    return request({ method: "POST", url: `/test-runs/${id}/advance` });
  }

  cancelTestRun(id: number): Promise<TestRun> {
    return request({ method: "POST", url: `/test-runs/${id}/cancel` });
  }

  /** Renames a run or rewrites its summary of findings. */
  updateTestRun(id: number, body: UpdateTestRunRequest): Promise<TestRun> {
    return request({ method: "PATCH", url: `/test-runs/${id}`, data: body });
  }

  /** Downloads the question list as an .xlsx, for a coach to review. */
  exportPromptCollection(id: number, filename: string): Promise<void> {
    return download(`/prompt-collections/${id}/export`, filename);
  }

  /** Downloads the run's results as an .xlsx. */
  exportTestRun(id: number, filename: string): Promise<void> {
    return download(`/test-runs/${id}/export`, filename);
  }

  compareTestRuns(a: number, b: number): Promise<TestRunComparison> {
    return request({
      method: "GET",
      url: "/test-runs/compare",
      params: { a, b },
    });
  }

  /** A second model reviews every answered prompt, in the background. */
  requestTestRunAiReview(runId: number): Promise<TestRunAiReviewResponse> {
    return request({ method: "POST", url: `/test-runs/${runId}/ai-review` });
  }

  setTestVerdict(
    runId: number,
    answerId: number,
    body: SetTestVerdictRequest,
  ): Promise<TestRunAnswer> {
    return request({
      method: "PUT",
      url: `/test-runs/${runId}/answers/${answerId}/verdict`,
      data: body,
    });
  }

  listSportReleases(): Promise<SportRelease[]> {
    return request({ method: "GET", url: "/releases" });
  }

  releaseSport(
    sportId: string,
    body: ReleaseSportRequest = {},
  ): Promise<SportRelease> {
    return request({
      method: "POST",
      url: `/releases/${sportId}/release`,
      data: body,
    });
  }

  withdrawSport(sportId: string, note?: string | null): Promise<SportRelease> {
    return request({
      method: "POST",
      url: `/releases/${sportId}/withdraw`,
      data: { note: note ?? null },
    });
  }
}

const aiCoachService = new AiCoachService();
export default aiCoachService;
